import { AfterViewInit, Directive, ElementRef, HostListener, OnDestroy, Renderer2 } from '@angular/core'
import { Router } from '@angular/router'
import { CmsService, EventService } from '@spartacus/core'
import { DownloadEvent } from '../../components/shared/gtm-event/download-event.events'
import { combineLatest, Subscription } from 'rxjs'
import { ClickEvent } from '../../components/shared/gtm-event/click-event.events'
import { CountryLanguageService } from '../../../site-context/facade/country-language.service'
import { MbNavigationService } from '../../navigation/mb-navigation.service'
import { CmsProductService } from '../../services/cms-product/cms-product.service'
import { MbEventListenerService } from '../../services/mb-event-listener/event-listener.service'
import { filter, map, take } from 'rxjs/operators'
import { ProductResolvedEvent } from '../../components/shared/events/product-resolved-event'

@Directive({
  selector: '[mbCmsInnerHTML]'
})
export class CmsInnerHtmlDirective implements OnDestroy, AfterViewInit {

  subscription = new Subscription()
  element: any

  constructor (private el: ElementRef,
               private router: Router,
               private events: EventService,
               private renderer: Renderer2,
               private countryLanguageService: CountryLanguageService,
               private navigationService: MbNavigationService,
               private cmsService: CmsService,
               private cmsProductService: CmsProductService,
               private eventService: MbEventListenerService) {
  }

  ngAfterViewInit () {
    this.modifyLinks()
  }

  modifyLinks (): void {
    this.subscription.add(combineLatest([this.countryLanguageService.getActive(), this.cmsService.getCurrentPage()])
      .subscribe(([languageData, currentPage]) => {
        this.el?.nativeElement?.querySelectorAll('a.cms-content-link')?.forEach((link: HTMLAnchorElement) => {
          const url = '/' + this.navigationService.getSeoUrlOfPageRef(link.getAttribute('data-reference') ?? '')
          this.setHref(link, languageData, url)
        })
        this.el?.nativeElement?.querySelectorAll('a.cms-anchor-link')?.forEach((link: HTMLAnchorElement) => {
          const anchor = link.getAttribute('data-anchor')
          const url = `/${currentPage?.properties?.formData?.pt_seoUrl?.value ?? ''}${anchor?.length ? '#' + anchor : ''}`
          this.setHref(link, languageData, url)
        })
        this.el?.nativeElement?.querySelectorAll('a.cms-product-link')?.forEach((link: HTMLAnchorElement) => {
          const productCode = link.getAttribute('data-product-code') ?? ''
          this.cmsProductService.batchProductRequests(productCode)
          this.eventService.retrieve()
            .pipe(
              filter(event => event?.event instanceof ProductResolvedEvent && event?.event?.productCode === productCode))
            .pipe(
              take(1),
              map(event => event?.event as ProductResolvedEvent))
            .subscribe(event => this.setHref(link, languageData, event?.url, true))
        })
      })
    )
  }

  @HostListener('click', ['$event'])
  onClick (event: Event): void {
    const element = event.target as HTMLElement
    const parentElement = element?.parentElement as HTMLElement
    if (!element) {
      return
    }
    if (element instanceof HTMLAnchorElement || parentElement instanceof HTMLAnchorElement) {
      this.handleLinkClick(element, event)
    }
  }

  private handleLinkClick (element: HTMLElement, event: Event) {
    const url = element.getAttribute('href') ?? ''
    const download = element.getAttribute('download')
    if (download) {
      let tagName = ''
      if (element.parentElement) {
        tagName = element.parentElement.getAttribute('data-tagname') ?? 'PDF'
      }
      this.events.dispatch(new DownloadEvent(tagName, download))
      return
    }
    switch (element.className) {
      case 'cms-anchor-link': {
        event.preventDefault()
        this.events.dispatch(new ClickEvent('Anchors', (element as any)?.text, ''))
        this.router.navigate([], { fragment: (element.getAttribute('data-anchor') ?? '') })
        break
      }
      case 'cms-document-link': {
        const assetType = element.getAttribute('data-assettype') ?? ''
        const fileName = element.getAttribute('data-filename') ?? ''
        this.events.dispatch(new DownloadEvent(assetType, fileName))
        break
      }
      case 'cms-product-link': {
        event.preventDefault()
        this.router.navigate([url.split('#')[0].split('/').slice(2).join('/')], { fragment: url.split('#')[1] })
        break
      }
      case 'cms-content-link': {
        event.preventDefault()
        this.router.navigate([url.split('/').splice(2).join('/')])
        break
      }
    }
  }

  private setHref (link: HTMLAnchorElement, countryLanguage: string, href: string = '', isProduct: boolean = false) {
    href = isProduct ? href : href.split('#').shift() ?? ''
    this.renderer.setAttribute(link, 'href', `/${countryLanguage}${href}`)
  }

  ngOnDestroy () {
    this.subscription.unsubscribe()
  }
}
